import { useEffect, useRef, useState } from 'react';

import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import {
  formatCubicBezier,
  resolveBezierPoints,
} from '../SegmentedControl/SegmentedControl.animation';
import type { BottomSheetStepsProps } from './BottomSheet.types';

const EASING = formatCubicBezier(resolveBezierPoints(undefined, undefined));

/**
 * Multi-step body slider — WEB. Every step is laid out in the same box; only
 * the active one participates in flow (so the sheet hugs its height), the rest
 * are absolutely positioned and translated a full width to the left/right.
 * Advancing the step slides the outgoing panel out to the left and the incoming
 * one in from the right; going back reverses it, because the transform is a
 * pure function of `index - step`.
 *
 * A CSS transform transition, not framer-motion — same reasoning as
 * SegmentedControl's pill: it keeps the shared package's dependency surface
 * minimal. Duration is a runtime prop, so the transition has to be an inline
 * style; `prefers-reduced-motion` is therefore read in JS rather than via
 * `motion-reduce:` (an inline style would win over the class).
 */
/** Inactive-panel blur when `blur` is on (native sibling: intensity 25). */
const PANEL_BLUR_PX = 8;

export function BottomSheetSteps({
  steps,
  step,
  duration,
  disableAnimation,
  blur = false,
  onStepSettled,
  className,
}: BottomSheetStepsProps) {
  const reducedMotion = usePrefersReducedMotion();
  const animate = !disableAnimation && !reducedMotion;
  // At rest on mount (nothing slid) and, with no transition, on every change;
  // otherwise when the active panel's own transform transition ends.
  const mounted = useRef(false);
  useEffect(() => {
    if (!animate || !mounted.current) onStepSettled?.(step);
    mounted.current = true;
  }, [step, animate, onStepSettled]);
  const transition = animate
    ? `transform ${duration}ms ${EASING}, opacity ${duration}ms ${EASING}` +
      (blur ? `, filter ${duration}ms ${EASING}` : '')
    : undefined;

  return (
    <View className={cn('relative w-full overflow-hidden', className)}>
      {steps.map((node, index) => {
        const isActive = index === step;
        return (
          <View
            // Steps are a positional list supplied by the caller; index is the
            // only stable identity available.
            key={index}
            className={cn('w-full', !isActive && 'absolute inset-x-0 top-0')}
            webProps={{
              'aria-hidden': isActive ? undefined : true,
              style: {
                transform: `translateX(${(index - step) * 100}%)`,
                opacity: isActive ? 1 : 0,
                ...(blur
                  ? { filter: isActive ? 'blur(0px)' : `blur(${PANEL_BLUR_PX}px)` }
                  : {}),
                pointerEvents: isActive ? undefined : 'none',
                transition,
              },
              onTransitionEnd: isActive
                ? (event: {
                    propertyName: string;
                    target: unknown;
                    currentTarget: unknown;
                  }) => {
                    if (
                      event.propertyName === 'transform' &&
                      event.target === event.currentTarget
                    )
                      onStepSettled?.(step);
                  }
                : undefined,
            }}>
            {node}
          </View>
        );
      })}
    </View>
  );
}
BottomSheetSteps.displayName = 'BottomSheetSteps';

/** `prefers-reduced-motion: reduce`, kept in sync with the media query. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}
